import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";

const WoahdaLanding = () => {
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate()

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const features = [
    {
      icon: "📱",
      title: "Sync Mobile Money",
      desc: "Importez automatiquement vos transactions Orange Money, MTN et Wave dans votre comptabilité.",
      color: "#F97316",
    },
    {
      icon: "📊",
      title: "Plan SYSCOHADA",
      desc: "Conforme au référentiel OHADA révisé 2017. Bilan, compte de résultat et TAFIRE générés automatiquement.",
      color: "#EAB308",
    },
    {
      icon: "🏛️",
      title: "Liasse Fiscale",
      desc: "Remplissez vos déclarations fiscales (IS, TVA) directement depuis votre logiciel.",
      color: "#22C55E",
    },
    {
      icon: "🤖",
      title: "IA Comptable",
      desc: "Détection automatique des erreurs de classement et suggestions de corrections en temps réel.",
      color: "#F97316",
    },
    {
      icon: "📈",
      title: "Tableau de bord",
      desc: "Visualisez votre trésorerie, vos ratios financiers et vos SIG en un coup d'œil.",
      color: "#EAB308",
    },
    {
      icon: "🎓",
      title: "Mode Formation",
      desc: "Simulateur pédagogique avec un dirigeant IA pour apprendre à présenter votre bilan.",
      color: "#22C55E",
    },
  ];

  const plans = [
    {
      name: "Starter",
      price: "9 990",
      desc: "Pour les étudiants et auto-entrepreneurs",
      features: ["1 dossier entreprise", "Sync Mobile Money", "Bilan & Résultat", "Support email"],
      accent: "#F97316",
      popular: false,
    },
    {
      name: "Pro",
      price: "24 990",
      desc: "Pour les PME et cabinets",
      features: ["5 dossiers entreprise", "Sync Mobile Money", "Liasse fiscale complète", "Mode Formation IA", "Support prioritaire"],
      accent: "#EAB308",
      popular: true,
    },
    {
      name: "Cabinet",
      price: "59 990",
      desc: "Pour les cabinets d'expertise",
      features: ["Dossiers illimités", "Multi-utilisateurs", "Tout Pro inclus", "API dédiée", "Onboarding personnalisé"],
      accent: "#22C55E",
      popular: false,
    },
  ];

  const stats = [
    { value: "17", label: "Pays OHADA couverts" },
    { value: "100%", label: "Conforme SYSCOHADA 2017" },
    { value: "3", label: "Opérateurs Mobile Money" },
    { value: "0€", label: "Pour démarrer" },
  ];

  return (
    <div
      style={{
        fontFamily: "'Sora', 'Segoe UI', sans-serif",
        background: "#0A0A0A",
        color: "#F5F0E8",
        overflowX: "hidden",
      }}
    >
      {/* Import Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .nav-link {
          color: #9A8F7E;
          text-decoration: none;
          font-size: 0.875rem;
          transition: color 0.2s;
          cursor: pointer;
        }
        .nav-link:hover { color: #F5F0E8; }

        .feature-card {
          background: #111109;
          border: 1px solid #1E1C14;
          border-radius: 20px;
          padding: 32px;
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }
        .feature-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, transparent 60%, rgba(249,115,22,0.03));
          opacity: 0;
          transition: opacity 0.3s;
        }
        .feature-card:hover {
          border-color: #2E2A1E;
          transform: translateY(-4px);
          box-shadow: 0 20px 60px rgba(0,0,0,0.4);
        }
        .feature-card:hover::before { opacity: 1; }

        .stat-item {
          text-align: center;
          padding: 24px;
        }

        .plan-card {
          background: #111109;
          border: 1px solid #1E1C14;
          border-radius: 24px;
          padding: 36px 32px;
          transition: all 0.3s;
          position: relative;
        }
        .plan-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 30px 80px rgba(0,0,0,0.5);
        }

        .badge-popular {
          position: absolute;
          top: -14px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #F97316, #EAB308);
          color: #0A0A0A;
          font-size: 0.72rem;
          font-weight: 700;
          padding: 5px 16px;
          border-radius: 50px;
          white-space: nowrap;
          letter-spacing: 0.05em;
        }

        .check-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.875rem;
          color: #9A8F7E;
          margin-bottom: 12px;
        }

        .geo-pattern {
          position: absolute;
          pointer-events: none;
          opacity: 0.04;
        }

        .ticker {
          display: flex;
          gap: 60px;
          animation: ticker 20s linear infinite;
          white-space: nowrap;
        }
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .fade-in {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeIn 0.8s ease forwards;
        }
        @keyframes fadeIn {
          to { opacity: 1; transform: translateY(0); }
        }

        .glow-orange {
          position: absolute;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(249,115,22,0.12) 0%, transparent 70%);
          pointer-events: none;
        }

        .mobile-mockup {
          background: #1A1710;
          border: 1px solid #2A2520;
          border-radius: 28px;
          padding: 20px;
          width: 260px;
          position: relative;
        }

        .transaction-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 0;
          border-bottom: 1px solid #1E1C14;
          font-size: 0.8rem;
        }

        @media (max-width: 768px) {
          .hero-grid { flex-direction: column !important; }
          .features-grid { grid-template-columns: 1fr !important; }
          .plans-grid { grid-template-columns: 1fr !important; }
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* NAV */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          padding: "16px 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: scrollY > 50 ? "rgba(10,10,10,0.95)" : "transparent",
          backdropFilter: scrollY > 50 ? "blur(20px)" : "none",
          borderBottom: scrollY > 50 ? "1px solid #1E1C14" : "none",
          transition: "all 0.3s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: 36,
              height: 36,
              background: "linear-gradient(135deg, #F97316, #EAB308)",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "1rem",
              color: "#0A0A0A",
            }}
          >
            W
          </div>
          <span style={{ fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.02em" }}>
            Woahda<span style={{ color: "#F97316" }}>.</span>
          </span>
        </div>

        <div style={{ display: "flex", gap: "32px", alignItems: "center" }}>
          <a className="nav-link">Fonctionnalités</a>
          <a className="nav-link">Tarifs</a>
          <a className="nav-link">À propos</a>
          <a className="nav-link">Contact</a>
        </div>

        <div style={{ display: "flex", gap: "12px" }}>
          <Button
            variant="ghost"
            onClick={() => navigate("/auth")}
            className="rounded-full border-[#2A2520] bg-transparent px-6 py-2.5 text-sm font-semibold text-[#F5F0E8] hover:border-[#9A8F7E] hover:bg-[#1A1710]"
          >
            Connexion
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/auth/cabinet")}
            className="rounded-full border-[#2A2520] bg-transparent px-6 py-2.5 text-sm font-semibold text-[#F5F0E8] hover:border-[#9A8F7E] hover:bg-[#1A1710]"
          >
            Connexion cabinet
          </Button>
          <Button variant="primary" onClick={() => navigate("/onboarding")} className="rounded-full px-6 py-2.5 text-sm font-semibold">
            Démarrer
          </Button>
        </div>
      </nav>

      {/* HERO */}
      <section
        ref={heroRef}
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          padding: "120px 40px 80px",
          overflow: "hidden",
        }}
      >
        {/* Background glows */}
        <div className="glow-orange" style={{ top: "-200px", left: "-200px" }} />
        <div
          style={{
            position: "absolute",
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(234,179,8,0.08) 0%, transparent 70%)",
            bottom: "-100px",
            right: "-100px",
            pointerEvents: "none",
          }}
        />

        {/* Geometric pattern */}
        <svg
          className="geo-pattern"
          style={{ top: "10%", right: "5%" }}
          width="400"
          height="400"
          viewBox="0 0 400 400"
        >
          {[0, 60, 120, 180, 240, 300].map((angle, i) => (
            <line
              key={i}
              x1="200"
              y1="200"
              x2={200 + 180 * Math.cos((angle * Math.PI) / 180)}
              y2={200 + 180 * Math.sin((angle * Math.PI) / 180)}
              stroke="#F97316"
              strokeWidth="1"
            />
          ))}
          <circle cx="200" cy="200" r="180" fill="none" stroke="#F97316" strokeWidth="1" />
          <circle cx="200" cy="200" r="120" fill="none" stroke="#EAB308" strokeWidth="0.5" />
          <circle cx="200" cy="200" r="60" fill="none" stroke="#F97316" strokeWidth="0.5" />
        </svg>

        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: "80px",
          }}
          className="hero-grid"
        >
          {/* Left */}
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: "#1A1710",
                border: "1px solid #2A2520",
                borderRadius: "50px",
                padding: "6px 16px",
                fontSize: "0.78rem",
                color: "#F97316",
                marginBottom: "28px",
                fontWeight: 600,
              }}
              className="fade-in"
            >
              <span style={{ width: 6, height: 6, background: "#F97316", borderRadius: "50%", display: "inline-block" }} />
              Conforme SYSCOHADA Révisé 2017
            </div>

            <h1
              style={{
                fontSize: "clamp(2.8rem, 5vw, 4.2rem)",
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
                marginBottom: "24px",
              }}
              className="fade-in"
            >
              La comptabilité{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #F97316, #EAB308)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                OHADA
              </span>
              <br />
              pensée pour{" "}
              <span style={{ color: "#2A2520", WebkitTextStroke: "1px #9A8F7E" }}>
                l'Afrique
              </span>
            </h1>

            <p
              style={{
                fontSize: "1.1rem",
                color: "#9A8F7E",
                lineHeight: 1.7,
                marginBottom: "40px",
                maxWidth: "520px",
              }}
              className="fade-in"
              // @ts-ignore
              style2={{ animationDelay: "0.1s" }}
            >
              Synchronisez vos transactions Mobile Money, gérez votre comptabilité
              SYSCOHADA et préparez vos déclarations fiscales — le tout depuis
              une seule plateforme pensée pour les entreprises africaines.
            </p>

            <div
              style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "48px" }}
              className="fade-in"
            >
              <Button variant="primary" onClick={() => navigate("/onboarding")} className="rounded-full px-8 py-3 text-sm font-semibold">
                Démarrer →
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="rounded-full border-[#2A2520] bg-transparent px-8 py-3 text-sm font-semibold text-[#F5F0E8] hover:border-[#9A8F7E] hover:bg-[#1A1710]"
              >
                Voir la démo
              </Button>
            </div>

            {/* Operators */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                fontSize: "0.8rem",
                color: "#5A5040",
              }}
            >
              <span>Compatible avec</span>
              {["Orange Money", "MTN MoMo", "Wave"].map((op) => (
                <span
                  key={op}
                  style={{
                    background: "#1A1710",
                    border: "1px solid #2A2520",
                    borderRadius: "6px",
                    padding: "4px 10px",
                    color: "#9A8F7E",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                >
                  {op}
                </span>
              ))}
            </div>
          </div>

          {/* Right - Dashboard mockup */}
          <div style={{ flex: "0 0 auto", position: "relative" }}>
            <div
              style={{
                background: "#111109",
                border: "1px solid #2A2520",
                borderRadius: "24px",
                padding: "24px",
                width: "380px",
                boxShadow: "0 40px 100px rgba(0,0,0,0.6)",
              }}
            >
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                  <div style={{ fontSize: "0.72rem", color: "#5A5040", marginBottom: "4px" }}>Solde Total</div>
                  <div style={{ fontSize: "1.6rem", fontWeight: 800, letterSpacing: "-0.02em" }}>
                    12 450 000{" "}
                    <span style={{ fontSize: "0.9rem", color: "#9A8F7E", fontWeight: 400 }}>FCFA</span>
                  </div>
                </div>
                <div
                  style={{
                    background: "rgba(34,197,94,0.1)",
                    color: "#22C55E",
                    borderRadius: "8px",
                    padding: "6px 12px",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                  }}
                >
                  +8.2% ↑
                </div>
              </div>

              {/* Mini chart */}
              <div
                style={{
                  background: "#0A0A08",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "16px",
                  height: "80px",
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "6px",
                }}
              >
                {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 100].map((h, i) => (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      height: `${h}%`,
                      background: i === 11
                        ? "linear-gradient(180deg, #F97316, #EAB308)"
                        : "#1E1C14",
                      borderRadius: "3px 3px 0 0",
                    }}
                  />
                ))}
              </div>

              {/* Transactions */}
              <div style={{ fontSize: "0.72rem", color: "#5A5040", marginBottom: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Dernières transactions
              </div>
              {[
                { label: "Orange Money — Client Koné", amount: "+450 000", positive: true, op: "🟠" },
                { label: "Fournisseur Matériaux SA", amount: "-180 000", positive: false, op: "🔴" },
                { label: "MTN MoMo — Vente marché", amount: "+230 000", positive: true, op: "🟡" },
                { label: "Wave — Règlement facture", amount: "+95 000", positive: true, op: "🔵" },
              ].map((tx, i) => (
                <div key={i} className="transaction-row">
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "1rem" }}>{tx.op}</span>
                    <span style={{ color: "#9A8F7E", maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {tx.label}
                    </span>
                  </div>
                  <span style={{ color: tx.positive ? "#22C55E" : "#EF4444", fontWeight: 700, fontFamily: "'Space Mono', monospace" }}>
                    {tx.amount}
                  </span>
                </div>
              ))}

              {/* Bottom badges */}
              <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                {["Compte 411", "Compte 401", "Compte 512"].map((c) => (
                  <span
                    key={c}
                    style={{
                      background: "#1A1710",
                      border: "1px solid #2A2520",
                      borderRadius: "6px",
                      padding: "4px 8px",
                      fontSize: "0.65rem",
                      color: "#F97316",
                      fontFamily: "'Space Mono', monospace",
                    }}
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            {/* Floating badge */}
            <div
              style={{
                position: "absolute",
                top: "-20px",
                right: "-20px",
                background: "linear-gradient(135deg, #F97316, #EAB308)",
                borderRadius: "14px",
                padding: "12px 16px",
                color: "#0A0A0A",
                fontWeight: 800,
                fontSize: "0.8rem",
                boxShadow: "0 8px 30px rgba(249,115,22,0.4)",
              }}
            >
              ✓ SYSCOHADA
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div
        style={{
          background: "#111109",
          borderTop: "1px solid #1E1C14",
          borderBottom: "1px solid #1E1C14",
          padding: "14px 0",
          overflow: "hidden",
        }}
      >
        <div className="ticker">
          {[...Array(2)].map((_, j) =>
            ["OHADA • 17 PAYS", "ORANGE MONEY ✓", "MTN MOMO ✓", "WAVE ✓", "SYSCOHADA 2017 ✓", "IS • TVA • LIASSE FISCALE", "BILAN • COMPTE DE RÉSULTAT • TAFIRE", "CÔTE D'IVOIRE • SÉNÉGAL • CAMEROUN"].map((item, i) => (
              <span
                key={`${j}-${i}`}
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: i % 2 === 0 ? "#F97316" : "#5A5040",
                  letterSpacing: "0.1em",
                  fontFamily: "'Space Mono', monospace",
                }}
              >
                {item}
              </span>
            ))
          )}
        </div>
      </div>

      {/* STATS */}
      <section style={{ padding: "80px 40px" }}>
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "1px",
            background: "#1E1C14",
            border: "1px solid #1E1C14",
            borderRadius: "20px",
            overflow: "hidden",
          }}
          className="stats-grid"
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className="stat-item"
              style={{ background: "#0A0A0A", padding: "40px 24px" }}
            >
              <div
                style={{
                  fontSize: "clamp(2rem, 4vw, 3rem)",
                  fontWeight: 800,
                  background: "linear-gradient(135deg, #F97316, #EAB308)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.03em",
                  marginBottom: "8px",
                  fontFamily: "'Space Mono', monospace",
                }}
              >
                {stat.value}
              </div>
              <div style={{ fontSize: "0.85rem", color: "#5A5040" }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section style={{ padding: "80px 40px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <div
              style={{
                display: "inline-block",
                fontSize: "0.75rem",
                color: "#F97316",
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                marginBottom: "16px",
                fontFamily: "'Space Mono', monospace",
              }}
            >
              FONCTIONNALITÉS
            </div>
            <h2
              style={{
                fontSize: "clamp(2rem, 4vw, 3rem)",
                fontWeight: 800,
                letterSpacing: "-0.03em",
                lineHeight: 1.1,
              }}
            >
              Tout ce dont vous avez besoin
              <br />
              <span style={{ color: "#2A2520", WebkitTextStroke: "1px #5A5040" }}>pour gérer votre compta</span>
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "16px",
            }}
            className="features-grid"
          >
            {features.map((f, i) => (
              <div key={i} className="feature-card">
                <div
                  style={{
                    width: 48,
                    height: 48,
                    background: `${f.color}15`,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.4rem",
                    marginBottom: "20px",
                    border: `1px solid ${f.color}25`,
                  }}
                >
                  {f.icon}
                </div>
                <h3
                  style={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    marginBottom: "10px",
                    color: "#F5F0E8",
                  }}
                >
                  {f.title}
                </h3>
                <p style={{ fontSize: "0.85rem", color: "#5A5040", lineHeight: 1.6 }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section style={{ padding: "80px 40px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#F97316",
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                marginBottom: "16px",
                fontFamily: "'Space Mono', monospace",
              }}
            >
              TARIFS
            </div>
            <h2
              style={{
                fontSize: "clamp(2rem, 4vw, 3rem)",
                fontWeight: 800,
                letterSpacing: "-0.03em",
              }}
            >
              Simple et transparent
            </h2>
            <p style={{ color: "#5A5040", marginTop: "12px", fontSize: "0.95rem" }}>
              Tarifs en FCFA / mois · Sans engagement
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "20px",
              alignItems: "center",
            }}
            className="plans-grid"
          >
            {plans.map((plan, i) => (
              <div
                key={i}
                className="plan-card"
                style={{
                  border: plan.popular ? `1px solid ${plan.accent}40` : "1px solid #1E1C14",
                  transform: plan.popular ? "scale(1.03)" : "scale(1)",
                  background: plan.popular ? "#14120A" : "#111109",
                }}
              >
                {plan.popular && (
                  <div className="badge-popular">⭐ PLUS POPULAIRE</div>
                )}
                <div
                  style={{
                    fontSize: "0.78rem",
                    color: plan.accent,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    marginBottom: "12px",
                    fontFamily: "'Space Mono', monospace",
                  }}
                >
                  {plan.name}
                </div>
                <div
                  style={{
                    fontSize: "2.2rem",
                    fontWeight: 800,
                    letterSpacing: "-0.03em",
                    marginBottom: "4px",
                    fontFamily: "'Space Mono', monospace",
                  }}
                >
                  {plan.price}
                  <span style={{ fontSize: "0.9rem", color: "#5A5040", fontWeight: 400, fontFamily: "'Sora', sans-serif" }}>
                    {" "}FCFA
                  </span>
                </div>
                <p style={{ fontSize: "0.8rem", color: "#5A5040", marginBottom: "28px" }}>
                  {plan.desc}
                </p>
                <div style={{ marginBottom: "28px" }}>
                  {plan.features.map((feat, j) => (
                    <div key={j} className="check-item">
                      <span style={{ color: plan.accent, fontWeight: 700 }}>✓</span>
                      {feat}
                    </div>
                  ))}
                </div>
                <button
                  style={{
                    width: "100%",
                    padding: "13px",
                    borderRadius: "12px",
                    border: plan.popular ? "none" : `1px solid ${plan.accent}40`,
                    background: plan.popular
                      ? `linear-gradient(135deg, ${plan.accent}, #EAB308)`
                      : "transparent",
                    color: plan.popular ? "#0A0A0A" : plan.accent,
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    fontFamily: "'Sora', sans-serif",
                    transition: "all 0.3s",
                  }}
                >
                  Commencer →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "80px 40px" }}>
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            background: "linear-gradient(135deg, #1A1408, #14120A)",
            border: "1px solid #2A2520",
            borderRadius: "32px",
            padding: "80px 60px",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-100px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "400px",
              height: "400px",
              background: "radial-gradient(circle, rgba(249,115,22,0.1) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <h2
            style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              marginBottom: "16px",
              position: "relative",
            }}
          >
            Prêt à formaliser votre{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #F97316, #EAB308)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              comptabilité ?
            </span>
          </h2>
          <p
            style={{
              color: "#5A5040",
              fontSize: "1rem",
              lineHeight: 1.7,
              marginBottom: "36px",
              position: "relative",
            }}
          >
            Rejoignez les entreprises africaines qui utilisent Woahda Compta
            pour gérer leur comptabilité OHADA en toute sérénité.
          </p>
          <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap", position: "relative" }}>
            <Button variant="primary" onClick={() => navigate("/onboarding")} className="rounded-full px-8 py-3 text-sm font-semibold">
              Créer mon compte gratuit →
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="rounded-full border-[#2A2520] bg-transparent px-8 py-3 text-sm font-semibold text-[#F5F0E8] hover:border-[#9A8F7E] hover:bg-[#1A1710]"
            >
              Parler à un expert
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        style={{
          borderTop: "1px solid #1E1C14",
          padding: "40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: 28,
              height: 28,
              background: "linear-gradient(135deg, #F97316, #EAB308)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "0.8rem",
              color: "#0A0A0A",
            }}
          >
            W
          </div>
          <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>
            Woahda<span style={{ color: "#F97316" }}>.</span>
          </span>
          <span style={{ color: "#2A2520", fontSize: "0.8rem", marginLeft: "8px" }}>
            © 2025 · Tous droits réservés
          </span>
        </div>

        <div style={{ display: "flex", gap: "24px" }}>
          {["CGU", "Confidentialité", "Contact", "Documentation"].map((link) => (
            <a key={link} className="nav-link" style={{ fontSize: "0.8rem" }}>
              {link}
            </a>
          ))}
        </div>

        <div style={{ fontSize: "0.78rem", color: "#2A2520" }}>
          Fait avec ❤️ pour l'Afrique
        </div>
      </footer>
    </div>
  );
};

export default WoahdaLanding;